/**
 * "Situação da conta" on the person record (D580): where the account stands today — its timed suspensions (what, why, from and until
 * when), on a rider the debts and the cash block, and the contextual safety flags. Each of the three reads its own route and says on
 * its own when it could not be read, so a missing permission on one never hides the others.
 */
import { type ReactElement } from 'react';
import { ok, type Result } from '@vello/core';
import { type ApiError, type DirectoryUser, type RiderDebtHistoryView, type SuspensionStatusView } from '@vello/api-client';
import { Badge, Button, Ink, RecordPanel, RecordRow, RecordRowButton, Text } from '@vello/web-theme';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { screenAllowed } from '../../src/index';
import { CardSection, useCardRead } from './person-card';
import { usePersonFormat } from './person-format';

const SUSPENSIONS_LIMIT = 20;
const DEBTS_SHOWN = 5;
const FLAGS_SHOWN = 5;

const suspensionTone = (status: SuspensionStatusView | undefined): 'danger' | 'warning' | 'neutral' => (status === 'ACTIVE' ? 'danger' : status === 'SCHEDULED' ? 'warning' : 'neutral');

export function PersonStandingCard({ user, isRider }: { user: DirectoryUser; isRider: boolean }): ReactElement {
  const { runtime, scope, grants } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();
  const { v } = useVocab();
  const fmt = usePersonFormat();
  const market = scope.current.city;
  const realm = isRider ? 'RIDER' : 'DRIVER';

  const suspensions = useCardRead(
    () => runtime.reputation.suspensions(realm, { country: user.countryCode, userId: user.id, filter: 'ALL', limit: SUSPENSIONS_LIMIT }),
    [runtime, realm, user.countryCode, user.id],
  );
  // A rider the market has no debt record for answers 404 — that is "no debts", not a failure.
  const debts = useCardRead<RiderDebtHistoryView | undefined>(
    isRider && market !== undefined
      ? async (): Promise<Result<RiderDebtHistoryView | undefined, ApiError>> => {
          const res = await runtime.riderDebts.history(user.id, market);
          return !res.ok && res.error.status === 404 ? ok(undefined) : res;
        }
      : undefined,
    [runtime, isRider, user.id, market],
  );
  const flags = useCardRead(() => runtime.contextualSafety.listFlags(user.id), [runtime, user.id]);

  return (
    <RecordPanel
      title={t('prec.standing.title')}
      actions={
        screenAllowed(grants, 'reputation_suspensions') ? (
          <Button variant="ghost" size="sm" onClick={() => navigate('reputation_suspensions')}>
            {t('prec.standing.allSuspensions')}
          </Button>
        ) : undefined
      }
    >
      <CardSection
        title={t('prec.standing.suspensions')}
        what={t('prec.what.suspensions')}
        state={suspensions.state}
        reload={suspensions.reload}
        isEmpty={(r) => r.suspensions.length === 0}
        empty={t('prec.standing.suspensions.empty')}
      >
        {(r) => (
          <>
            {r.suspensions.map((s) => (
              <div key={s.suspensionId}>
                <RecordRow dense>
                  <Badge tone={suspensionTone(s.status)}>{v('reputation.suspension_status', s.status)}</Badge>
                  <span>{v('reputation.suspension_reason', s.reasonCode)}</span>
                  <span className="vello-grow" />
                  <Ink mono>{fmt.when(s.startsAt)}</Ink>
                </RecordRow>
                <Text as="p" role="caption" tone="secondary">
                  {s.endsAt !== undefined ? t('prec.standing.until', { when: fmt.when(s.endsAt) }) : t('prec.standing.indefinite')}
                  {s.liftedAt !== undefined ? ` · ${t('prec.standing.lifted', { when: fmt.when(s.liftedAt) })}` : ''}
                </Text>
                {s.note !== undefined && s.note !== '' && (
                  <Text as="p" role="caption" tone="secondary">
                    {s.note}
                  </Text>
                )}
              </div>
            ))}
          </>
        )}
      </CardSection>

      {isRider && (
        <CardSection
          title={t('prec.standing.debts')}
          what={t('prec.what.debts')}
          state={debts.state}
          reload={debts.reload}
          heldBack={t('prec.needsMarket')}
        >
          {(h) => (
            <>
              {h === undefined ? (
                <Text as="p" role="caption" tone="secondary">
                  {t('prec.standing.debts.none')}
                </Text>
              ) : (
                <>
                  <RecordRow dense>
                    <Badge tone={h.cash.blocked ? 'danger' : 'success'}>{t(h.cash.blocked ? 'prec.standing.cash.blocked' : 'prec.standing.cash.free')}</Badge>
                    <Text role="caption" tone="secondary">
                      {t('prec.standing.cash.counted', { counted: String(h.cash.counted), after: String(h.cash.blockAfter) })}
                    </Text>
                  </RecordRow>
                  {h.debts.length === 0 ? (
                    <Text as="p" role="caption" tone="secondary">
                      {t('prec.standing.debts.none')}
                    </Text>
                  ) : (
                    h.debts.slice(0, DEBTS_SHOWN).map((d) => {
                      const body = (
                        <>
                          <Badge tone={d.status === 'SETTLED' ? 'success' : d.status === 'PARTIAL' ? 'warning' : 'danger'}>{v('debt.status', d.status)}</Badge>
                          <span>{v('debt.reason', d.reasonCode)}</span>
                          <span className="vello-grow" />
                          <Ink mono>{fmt.money({ amountMinor: d.amountMinor, currency: d.currency })}</Ink>
                          <Ink mono>{fmt.when(d.createdAt)}</Ink>
                        </>
                      );
                      return d.originRideId !== null && screenAllowed(grants, 'ride_detail') ? (
                        <RecordRowButton key={d.debtId} aria-label={t('prides.open', { code: d.originRideCode ?? d.originRideId })} onClick={() => navigate('ride_detail', { id: d.originRideId as string })}>
                          {body}
                        </RecordRowButton>
                      ) : (
                        <RecordRow key={d.debtId} dense>
                          {body}
                        </RecordRow>
                      );
                    })
                  )}
                  {h.truncated && (
                    <Text as="p" role="caption" tone="secondary">
                      {t('prec.standing.debts.more')}
                    </Text>
                  )}
                </>
              )}
            </>
          )}
        </CardSection>
      )}

      <CardSection
        title={t('prec.standing.flags')}
        what={t('prec.what.flags')}
        state={flags.state}
        reload={flags.reload}
        isEmpty={(r) => r.flags.length === 0}
        empty={t('prec.standing.flags.empty')}
      >
        {(r) => (
          <>
            {r.flags.slice(0, FLAGS_SHOWN).map((f) => (
              <div key={f.flagId}>
                <RecordRow dense>
                  <Badge tone={f.state === 'ACTIVE' ? 'danger' : 'neutral'}>{v('safety.flag_state', f.state)}</Badge>
                  <span>{v('safety.flag_reason', f.reasonCode)}</span>
                  <span className="vello-grow" />
                  <Ink mono>{fmt.when(f.raisedAt)}</Ink>
                </RecordRow>
                {f.clearedAt !== undefined && (
                  <Text as="p" role="caption" tone="secondary">
                    {t('prec.standing.flags.cleared', { when: fmt.when(f.clearedAt) })}
                    {f.clearReason !== undefined && f.clearReason !== '' ? ` · ${f.clearReason}` : ''}
                  </Text>
                )}
              </div>
            ))}
          </>
        )}
      </CardSection>
    </RecordPanel>
  );
}
